import { oc } from "@orpc/contract";
import { z } from "zod";
import { CatalogVersionSchema, TemplateDetailSchema } from "../catalog-detail.js";
import { CatalogFiltersSchema, CatalogListCountsSchema, CatalogSortSchema, } from "../catalog-shelf.js";
import { LocaleSchema } from "../sites.js";
import { CatalogFacetsSchema, CatalogTemplateSchema } from "../wire.js";
const contentKey = z
    .string()
    .max(80)
    .regex(/^[a-z0-9]+(?:[_-][a-z0-9]+)*$/);
// The catalog members browse: the platform libraries' live versions, in the viewer's
// locale (or the site default when signed out). Both sites serve the same catalog: every
// template live on its home site is listed, opened and served on either host, and
// `siteId` on a row names that home site. Every procedure answers signed out;
// signed in, the same rows come back for the same inputs.
//
// The gallery does not page through `list`: it loads the whole shelf once from the
// cacheable snapshot (CATALOG_SNAPSHOT_PATH) and filters, counts and orders in the browser.
// `list` answers the same shelf for a client that does not: it reads the server's cached
// snapshot and runs the same shelf module (catalog-shelf.ts) over it (wire 5.21.0, H.25).
// `facets` and the snapshot list the same libraries (CatalogLibrarySchema, wire 5.15.0).
/**
 * The catalog snapshot: a plain GET beside the procedures (`${API_PREFIX}${CATALOG_SNAPSHOT_PATH}
 * ?locale=xx`), not a procedure, so a browser and any HTTP cache can keep it. It answers
 * CatalogSnapshotSchema for every live template, with an ETag that changes when the catalog
 * does (a publication, a retirement, a release), `Cache-Control: public, max-age=60,
 * stale-while-revalidate=600`, 304 on a matching If-None-Match, and a compressed body when the
 * request accepts one. Declared with the other hand-written routes as API_ROUTES.catalogSnapshot
 * (routes.ts).
 */
export const CATALOG_SNAPSHOT_PATH = "/catalog/snapshot";
export const catalog = {
    list: oc
        .route({
        summary: "The platform catalog, one page at a time",
        description: "Live template versions from both sites (each live on its home site, named by siteId) in the given locale (default: the viewer's, then the site's), served from the server's cached catalog snapshot through the shelf rules the gallery runs. With `shelf` (a shelf id from the snapshot's `shelves`; a first-party shelf's id is its site id) the list is that shelf: what it lists and excludes, its pinned templates first while no filter is set, its blanks hidden when it says so; an unknown id is 400 unknown_shelf, as the snapshot route answers it. Without it the list is every live template. `filters` narrow it (400 invalid_filters for inTeam with kind team), `sort` orders it (default recommended with a shelf, title without), and `counts` says how many rows each control would show against the other filters, the pinned rows left out. `total` is how many rows the list holds across its pages, and `catalogVersion` the snapshot's. Pages are at most `limit` rows (default 50); pass `nextCursor` back with the same locale, shelf, filters and sort for the next page: a cursor from another query is 400 invalid_cursor, and one from before the catalog changed (a publication, a retirement, a catalog-entry write, or a release that changed a shelf or the vocabulary) is 409 catalog_changed {catalogVersion}: read again from the first page.",
    })
        .meta({ public: true, access: "read" })
        .input(z.object({
        locale: LocaleSchema.optional(),
        shelf: contentKey.optional(),
        filters: CatalogFiltersSchema.optional(),
        sort: CatalogSortSchema.optional(),
        cursor: z.string().max(500).optional(),
        limit: z.number().int().min(1).max(100).optional(),
    }))
        .output(z.object({
        catalogVersion: z.string().min(1),
        templates: z.array(CatalogTemplateSchema),
        nextCursor: z.string().nullable(),
        total: z.number().int().nonnegative(),
        counts: CatalogListCountsSchema,
    })),
    facets: oc
        .route({
        summary: "The catalog's filters",
        description: "Every library that holds a live template, with its name and description in the request locale, its home site (siteId), its attribution (null when it declares none) and how many of its templates are live, and the vocabulary both sites share (categories, industries, keywords and capabilities) in the request locale. A library's own categories and tags are always empty: read the vocabulary.",
    })
        .meta({ public: true, access: "read" })
        .input(z.object({ locale: LocaleSchema.optional() }))
        .output(CatalogFacetsSchema),
    detail: oc
        .route({
        summary: "One template's detail",
        description: "The canonical detail, signed in or out: by library key and slug the live version, by templateVersionId any version of a live template. The prompt-free row the catalog lists (that version's own), plus whether it is the live version, its goal, its outcome, its starter files, its needs per provider and runner (served as this deployment serves them now), the live passes recorded on its content checksum its library with attribution and, when it copied from an upstream other than its library's, its source (wire 5.20.0). An unknown or invisible template is template_not_found.",
    })
        .meta({ public: true, access: "read" })
        .input(z.union([
        z.object({
            libraryKey: contentKey,
            slug: contentKey,
            locale: LocaleSchema.optional(),
        }),
        z.object({
            templateVersionId: z.string().uuid(),
            locale: LocaleSchema.optional(),
        }),
    ]))
        .output(z.object({ template: TemplateDetailSchema })),
    versions: oc
        .route({
        summary: "A template's versions",
        description: "Every version of a live template, newest first: its id, number, when it was written, whether it is the live one and its content checksum (what a tested-ledger entry names). An unknown or invisible template is template_not_found (wire 5.20.0).",
    })
        .meta({ public: true, access: "read" })
        .input(z.object({ libraryKey: contentKey, slug: contentKey }))
        .output(z.object({ versions: z.array(CatalogVersionSchema) })),
    // The read-only YAML view: the authored spec rendered, never the raw document.
    yaml: oc
        .route({
        summary: "A template version's YAML",
        description: "The authored spec of one template version rendered as read-only YAML for the detail page, never the raw stored document.",
    })
        .meta({ public: true, access: "read" })
        .input(z.object({ templateVersionId: z.string().uuid() }))
        .output(z.object({ templateVersionId: z.string().uuid(), yaml: z.string() })),
};
//# sourceMappingURL=catalog.js.map